import React from 'react';
import { TaskItem } from '../../types/topic';
import { TaskDeleteHandler, TaskSection, TaskUpdateHandler } from './types/dashboardTypes';
import DashboardTaskCard from './DashboardTaskCard';
import { LucideIcon } from 'lucide-react';

interface DashboardTaskColumnProps {
  title: string;
  icon: LucideIcon;
  iconBgColor: string;
  iconColor: string;
  tasks: TaskItem[];
  sectionType: TaskSection;
  projects: Array<{ id: string; name: string }>;
  isMobile: boolean;
  onEditTask: (task: TaskItem) => void;
  onUpdateTask: TaskUpdateHandler;
  onDeleteTask: TaskDeleteHandler;
  onNavigateToTask: (task: TaskItem) => void;
  children?: React.ReactNode; // Allow content override through children
}

const DashboardTaskColumn: React.FC<DashboardTaskColumnProps> = ({
  title,
  icon: Icon,
  iconBgColor,
  iconColor,
  tasks,
  sectionType,
  projects,
  isMobile,
  onEditTask,
  onUpdateTask,
  onDeleteTask,
  onNavigateToTask,
  children,
}) => {
  return (
    <div className="flex flex-col h-full">
      <div className={`flex ${isMobile ? 'justify-center mt-5' : ''} items-center gap-2 mb-3`}>
        {!isMobile && (
          <div className={`p-1.5 ${iconBgColor} rounded-lg`}>
            <Icon size={18} className={iconColor} />
          </div>
        )}
        <h2 className={`text-lg font-semibold ${iconColor}`}>
          {title}
          {!isMobile && (
            <span className="ml-2 text-sm font-medium bg-white text-gray-600 px-2 py-1 rounded-full">
              {tasks.length}
            </span>
          )}
        </h2>
      </div>
      <div className={`flex-1 ${isMobile ? '' : 'overflow-hidden'}`}>
        {/* If children are provided, render those instead of the default content */}
        {children ? (
          children
        ) : tasks.length > 0 ? (
          <div
            id={`${sectionType}-wrapper`}
            className={`scroll-container-wrapper rounded-lg ${isMobile ? '' : 'h-full'}`}
          >
            <div
              id={`${sectionType}-container`}
              className={`space-y-3 ${
                isMobile
                  ? 'overflow-visible pb-4' // Reduced padding for mobile
                  : 'overflow-y-auto pr-1 h-full max-h-[calc(100vh-160px)]'
              } scroll-container rounded-lg`}
            >
              {tasks.map(task => (
                <DashboardTaskCard
                  key={task.id}
                  task={task}
                  sectionType={sectionType}
                  projects={projects}
                  onEditTask={onEditTask}
                  onUpdateTask={onUpdateTask}
                  onDeleteTask={onDeleteTask}
                  onNavigateToTask={onNavigateToTask}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className={`text-center text-gray-500 py-8 flex flex-col items-center ${isMobile ? 'bg-white rounded-lg' : 'bg-[var(--content-bg-color)] rounded-lg border border-gray-200'}`}
          >
            <Icon size={24} className="text-gray-300 mb-2" />
            <p>No {title.toLowerCase()} tasks</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTaskColumn;
